import { z } from 'zod';

const APMC_CODE_REGEX = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;

export const apmcSchema = z.object({
  id: z.string(),
  code: z.string(),
  name: z.string(),
  districtId: z.string(),
  districtCode: z.string(),
  districtName: z.string(),
  stateId: z.string(),
  stateCode: z.string(),
  stateName: z.string(),
  address: z.string().nullable(),
  latitude: z.number().nullable(),
  longitude: z.number().nullable(),
  isActive: z.boolean(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type Apmc = z.infer<typeof apmcSchema>;

const apmcTranslationSchema = z.object({
  id: z.string(),
  localeId: z.string(),
  localeCode: z.string(),
  name: z.string(),
});
export const apmcDetailSchema = apmcSchema.extend({
  translations: z.array(apmcTranslationSchema),
});
export type ApmcDetail = z.infer<typeof apmcDetailSchema>;

export const apmcOptionSchema = z.object({
  id: z.string(),
  code: z.string(),
  name: z.string(),
});
export type ApmcOption = z.infer<typeof apmcOptionSchema>;

// Richer active-only option for M:N pickers that group APMCs by state
// (closure scope picker, future bulk-edit screens). Lives on /apmcs/picker-options.
export const apmcPickerOptionSchema = z.object({
  id: z.string(),
  code: z.string(),
  name: z.string(),
  state: z.object({ id: z.string(), code: z.string(), name: z.string() }),
  district: z.object({ id: z.string(), code: z.string(), name: z.string() }),
});
export type ApmcPickerOption = z.infer<typeof apmcPickerOptionSchema>;

// ────────────────────────────────────────────────────────────────────────────
// Form schema
// ────────────────────────────────────────────────────────────────────────────

export const apmcFormTranslationSchema = z.object({
  localeCode: z.string().min(1),
  localeName: z.string(),
  name: z.string().max(200),
});
export type ApmcFormTranslation = z.infer<typeof apmcFormTranslationSchema>;

// stateId is captured for the cascading filter only — server derives it from
// the chosen districtId. Stored as the option-id string.
export const apmcFormSchema = z.object({
  stateId: z.string().min(1, 'State is required'),
  districtId: z.string().min(1, 'District is required'),
  code: z
    .string()
    .min(1, 'Code is required')
    .max(100)
    .regex(APMC_CODE_REGEX, 'Lowercase alphanumerics joined by single hyphens'),
  name: z.string().min(1, 'Name is required').max(200),
  address: z.string().max(500).nullable(),
  // Number | null in form state. Server enforces Decimal(9,6) bounds.
  latitude: z.number().min(-90).max(90).nullable(),
  longitude: z.number().min(-180).max(180).nullable(),
  isActive: z.boolean(),
  translations: z.array(apmcFormTranslationSchema),
});
export type ApmcFormValues = z.infer<typeof apmcFormSchema>;

// ────────────────────────────────────────────────────────────────────────────
// List filters
// ────────────────────────────────────────────────────────────────────────────

export const APMC_SORT_KEYS = [
  'code',
  'name',
  'district',
  'state',
  'updated',
] as const;
export type ApmcSortKey = (typeof APMC_SORT_KEYS)[number];

export type SortDirection = 'asc' | 'desc';

export interface ListApmcsParams {
  page: number;
  pageSize: number;
  searchText?: string;
  stateId?: number;
  districtId?: number;
  isActive?: 'true' | 'false';
  sortBy?: ApmcSortKey;
  sortDir?: SortDirection;
}
